// Author: Sunil Jain
// Timestamp: 2025-04-30 03:25:00 PM EDT
// File: v4fe-physician/src/pages/ForgotPassword.js
// Description: Functional Forgot Password page.

import React, { useState } from 'react';
import {
    Avatar, Button, CssBaseline, TextField, Box, Typography, Container,
    CircularProgress, Alert, Link as MuiLink
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Link as RouterLink } from 'react-router-dom';
import logger from '../utils/logger';

// Define constants outside the component function
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
// CORRECTED: The '/api' prefix is removed to prevent duplication with API_BASE_URL.
const FORGOT_PASSWORD_ENDPOINT = '/users/request-password-reset';

function ForgotPassword() {
    logger.debug("ForgotPassword page rendered");
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setMessage('');
        setError('');
        logger.debug(`ForgotPassword: Requesting password reset for ${email}`);

        if (!email) {
            setError('Please enter your email address.');
            setLoading(false);
            return;
        }

        const fullURL = API_BASE_URL ? `${API_BASE_URL}${FORGOT_PASSWORD_ENDPOINT}` : FORGOT_PASSWORD_ENDPOINT;
        logger.info(`Sending password reset request to: ${fullURL}`);

        try {
            const response = await fetch(fullURL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email }),
            });

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData?.detail || `Request failed with status ${response.status}`);
            }

            logger.info("ForgotPassword: Request successful.", responseData);
            setMessage(responseData?.message || 'If an account exists for this email, a password reset link has been sent. Please check your inbox (and spam folder).');
        } catch (err) {
            logger.error('ForgotPassword error:', err);
            setError(err.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                    <LockResetIcon />
                </Avatar>
                <Typography component="h1" variant="h5">
                    Forgot Password
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Enter your email address below, and we'll send you a link to reset your password.
                </Typography>
                {message && (<Alert severity="success" sx={{ mt: 2, width: '100%' }}>{message}</Alert>)}
                {error && (<Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>)}
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, width: '100%' }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading || !!message}
                        error={!!error}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={loading || !!message}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                    </Button>
                     <Box textAlign="center">
                        <MuiLink component={RouterLink} to="/login" variant="body2">
                             Back to Sign In
                        </MuiLink>
                     </Box>
                </Box>
            </Box>
        </Container>
    );
}

export default ForgotPassword;